import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Statistic,
  Table,
  Spin,
  Breadcrumb,
  Tabs,
  Progress,
  Divider,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  TeamOutlined,
  DesktopOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Modal, Form, Select, InputNumber, DatePicker, Segmented, message, Empty } from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

// API helper
const apiCall = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  return response.json();
};

export default function CompanySnapshot() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Settlement Rules state
  const [settlementRules, setSettlementRules] = useState([]);
  const [availableRules, setAvailableRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm] = Form.useForm();

  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      try {
        const [companyRes, summaryRes] = await Promise.all([
          apiCall(`/api/companies/${companyId}`),
          apiCall(`/api/companies/${companyId}/account-summary`),
        ]);

        const companyData = companyRes?.data || {};
        const summaryData = summaryRes?.data || {};

        setCompany({
          id: companyData.id || companyId,
          name: companyData.company_name || companyData.name || "",
          type: companyData.company_type || "",
          status: companyData.status || "",
          contactName: companyData.contact_name || "",
          contactEmail: companyData.contact_email || "",
          contactPhone: companyData.contact_phone || "",
          createdAt: companyData.created_at || "",
          // Map summary data
          groups: summaryData.groups || 0,
          totalCustomers: summaryData.total_customers || 0,
          totalSystems: summaryData.total_systems || 0,
          totalKw: summaryData.total_kw || 0,
          totalKwh: summaryData.total_kwh || 0,
          onlineSystems: summaryData.online_systems || 0,
          offlineSystems: summaryData.offline_systems || 0,
          onlineRate: summaryData.online_rate || 0,
          totalRevenue: summaryData.total_revenue || 0,
          monthlyRevenue: summaryData.monthly_revenue || 0,
          avgSystemRevenue: summaryData.avg_system_revenue || 0,
          groupsList: summaryData.groups_list || [],
          inverterTypes: summaryData.inverter_types || [],
          batteryTypes: summaryData.battery_types || [],
          monthlyData: summaryData.monthly_data || [],
          realtimeData: summaryData.realtime_data || {},
        });
      } catch (err) {
        console.error("Failed to fetch company:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [companyId]);

  // Fetch settlement rules when tab changes to settlement
  useEffect(() => {
    if (activeTab === "settlement" && companyId) {
      fetchSettlementRules();
      fetchAvailableRules();
    }
  }, [activeTab, companyId]);

  const fetchSettlementRules = async () => {
    setRulesLoading(true);
    try {
      const data = await apiCall(`/api/settlements/bindings/company?entityId=${companyId}`);
      if (data.success) {
        setSettlementRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch settlement rules:", error);
    }
    setRulesLoading(false);
  };

  const fetchAvailableRules = async () => {
    try {
      const data = await apiCall("/api/settlements/rules?ruleType=COMPANY_REVENUE");
      if (data.success) {
        setAvailableRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch available rules:", error);
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    ruleForm.resetFields();
    ruleForm.setFieldsValue({
      effectiveFrom: dayjs(),
      priority: 100,
      isActive: true,
    });
    setRuleModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    ruleForm.setFieldsValue({
      ruleId: rule.rule_id,
      paramsOverride: rule.paramsOverride,
      effectiveFrom: rule.effective_from ? dayjs(rule.effective_from) : null,
      effectiveTo: rule.effective_to ? dayjs(rule.effective_to) : null,
      priority: rule.priority,
      isActive: rule.is_active,
    });
    setRuleModalVisible(true);
  };

  const handleDeleteRule = async (bindingId) => {
    try {
      const data = await apiCall(`/api/settlements/bindings/company/${bindingId}`, {
        method: "DELETE",
      });
      if (data.success) {
        message.success("Rule deleted successfully");
        fetchSettlementRules();
      } else {
        message.error(data.message || "Failed to delete rule");
      }
    } catch (error) {
      message.error("Failed to delete rule");
    }
  };

  const handleSaveRule = async () => {
    try {
      const values = await ruleForm.validateFields();
      const payload = {
        entityId: companyId,
        ruleId: values.ruleId,
        paramsOverride: values.paramsOverride || null,
        effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD"),
        effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
        priority: values.priority || 100,
        isActive: values.isActive !== false,
      };

      let data;
      if (editingRule) {
        data = await apiCall(`/api/settlements/bindings/company/${editingRule.binding_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        data = await apiCall("/api/settlements/bindings/company", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (data.success) {
        message.success(editingRule ? "Rule updated successfully" : "Rule added successfully");
        setRuleModalVisible(false);
        fetchSettlementRules();
      } else {
        message.error(data.message || "Failed to save rule");
      }
    } catch (error) {
      console.error("Save rule error:", error);
    }
  };

  if (loading) {
    return (
      <PageLayout active="companySnapshot">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!company) {
    return (
      <PageLayout active="companySnapshot">
        <div style={{ textAlign: "center", padding: 48 }}>
          <Text type="secondary">Company not found</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate("/company/list")}>
              Back to Company List
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const groupColumns = [
    {
      title: "Group",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Button type="link" onClick={() => navigate(`/group-management/groups/${record.id}`)} style={{ padding: 0 }}>
          {name}
        </Button>
      ),
    },
    { title: "Systems", dataIndex: "systems", key: "systems", align: "center" },
    { title: "Inverter (kW)", dataIndex: "kw", key: "kw", align: "right" },
    { title: "Battery (kWh)", dataIndex: "kwh", key: "kwh", align: "right" },
    {
      title: "Online",
      key: "online",
      align: "center",
      render: (_, record) => (
        <Space>
          <Tag color="success">{record.online}</Tag>
          <Tag color="error">{record.offline}</Tag>
        </Space>
      ),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      align: "right",
      render: (v) => <Text style={{ color: token.colorSuccess }}>${v.toFixed(2)}</Text>,
    },
  ];

  const tabItems = [
    {
      key: "overview",
      label: "Overview",
      children: (
        <div>
          {/* Summary Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Groups" value={company.groups} prefix={<TeamOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Customers" value={company.totalCustomers} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Systems" value={company.totalSystems} prefix={<DesktopOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                {(() => { const f = formatPower(company.totalKw || 0, "kW"); return (
                <Statistic title="Inverter" value={f.value} suffix={f.suffix} valueStyle={{ color: "#faad14" }} />); })()}
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                {(() => { const f = formatPower(company.totalKwh || 0, "kWh"); return (
                <Statistic title="Battery" value={f.value} suffix={f.suffix} valueStyle={{ color: "#f5a623" }} />); })()}
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Online Rate"
                  value={company.onlineRate}
                  suffix="%"
                  valueStyle={{ color: company.onlineRate >= 95 ? token.colorSuccess : token.colorWarning }}
                />
              </Card>
            </Col>
          </Row>

          {/* Online/Offline Status */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: 12 }} title="System Status">
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ textAlign: "center" }}>
                      <Progress
                        type="circle"
                        percent={company.onlineRate}
                        strokeColor={token.colorSuccess}
                        format={() => (
                          <div>
                            <CheckCircleOutlined style={{ fontSize: 24, color: token.colorSuccess }} />
                            <div style={{ fontSize: 20, fontWeight: 600 }}>{company.onlineSystems}</div>
                            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>Online</div>
                          </div>
                        )}
                        size={120}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ textAlign: "center" }}>
                      <Progress
                        type="circle"
                        percent={(company.offlineSystems / company.totalSystems) * 100}
                        strokeColor={token.colorError}
                        format={() => (
                          <div>
                            <CloseCircleOutlined style={{ fontSize: 24, color: token.colorError }} />
                            <div style={{ fontSize: 20, fontWeight: 600 }}>{company.offlineSystems}</div>
                            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>Offline</div>
                          </div>
                        )}
                        size={120}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: 12 }} title="Realtime Status">
                <Row gutter={16}>
                  <Col span={8}>
                    {(() => { const f = formatPower(company.realtimeData.totalPower || 0, "kW"); return (
                    <Statistic
                      title="Total Power"
                      value={f.value}
                      suffix={f.suffix}
                      valueStyle={{ color: token.colorSuccess }}
                    />); })()}
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg SOC"
                      value={company.realtimeData.avgSOC}
                      suffix="%"
                      valueStyle={{ color: "#f5a623" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Dispatching"
                      value={company.realtimeData.dispatchingNow}
                      valueStyle={{ color: token.colorPrimary }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: "16px 0" }} />
                <Text type="secondary">Last updated: {company.realtimeData.lastUpdate}</Text>
              </Card>
            </Col>
          </Row>

          {/* Revenue Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Total Revenue"
                  value={company.totalRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: token.colorSuccess, fontSize: 28 }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="This Month"
                  value={company.monthlyRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: token.colorPrimary, fontSize: 28 }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Avg per System"
                  value={company.avgSystemRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ fontSize: 28 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Groups Table */}
          <Card bordered={false} style={{ borderRadius: 12 }} title="Groups Breakdown">
            <Table
              columns={groupColumns}
              dataSource={company.groupsList}
              rowKey="id"
              pagination={false}
              summary={(data) => {
                const totals = data.reduce(
                  (acc, row) => ({
                    systems: acc.systems + row.systems,
                    kw: acc.kw + row.kw,
                    kwh: acc.kwh + row.kwh,
                    online: acc.online + row.online,
                    offline: acc.offline + row.offline,
                    revenue: acc.revenue + row.revenue,
                  }),
                  { systems: 0, kw: 0, kwh: 0, online: 0, offline: 0, revenue: 0 }
                );
                return (
                  <Table.Summary.Row style={{ background: token.colorFillQuaternary }}>
                    <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="center"><Text strong>{totals.systems}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><Text strong>{totals.kw}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><Text strong>{totals.kwh}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="center">
                      <Space>
                        <Tag color="success">{totals.online}</Tag>
                        <Tag color="error">{totals.offline}</Tag>
                      </Space>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ color: token.colorSuccess }}>${totals.revenue.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </div>
      ),
    },
    {
      key: "historical",
      label: "Historical Data",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Revenue">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                    <RechartsTooltip formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" stroke={token.colorSuccess} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Dispatches">
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip />
                    <Bar dataKey="dispatches" name="Dispatches" fill={token.colorPrimary} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Energy Discharged">
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => [`${v} kWh`, "Energy"]} />
                    <Bar dataKey="energy" name="Energy (kWh)" fill="#faad14" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: "equipment",
      label: "Equipment",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Inverter Distribution">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.inverterTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {company.inverterTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Divider />
              <Space wrap>
                {company.inverterTypes.map((item) => (
                  <Tag key={item.name} color={item.color}>
                    {item.name}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Battery Distribution">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.batteryTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {company.batteryTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Divider />
              <Space wrap>
                {company.batteryTypes.map((item) => (
                  <Tag key={item.name} color={item.color}>
                    {item.name}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: "settlement",
      label: (
        <span>
          <SettingOutlined /> Settlement Rules
        </span>
      ),
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Text type="secondary">
              Settlement rules determine how revenue is calculated for this company's default view group.
            </Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRule}>
              Add Rule
            </Button>
          </div>

          {rulesLoading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin />
            </div>
          ) : settlementRules.length === 0 ? (
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No settlement rules configured"
              >
                <Button type="primary" onClick={handleAddRule}>
                  Add Settlement Rule
                </Button>
              </Empty>
            </Card>
          ) : (
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Table
                dataSource={settlementRules}
                rowKey="binding_id"
                pagination={false}
                columns={[
                  {
                    title: "Rule",
                    key: "rule",
                    render: (_, record) => (
                      <div>
                        <Text strong>{record.rule_code}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.rule_description}</Text>
                      </div>
                    ),
                  },
                  {
                    title: "Parameters",
                    key: "params",
                    render: (_, record) => (
                      record.paramsOverride ? (
                        <Text code style={{ fontSize: 11 }}>
                          {JSON.stringify(record.paramsOverride)}
                        </Text>
                      ) : (
                        <Text type="secondary">Default</Text>
                      )
                    ),
                  },
                  {
                    title: "Effective Period",
                    key: "period",
                    render: (_, record) => (
                      <span>
                        {dayjs(record.effective_from).format("YYYY-MM-DD")}
                        {" → "}
                        {record.effective_to ? dayjs(record.effective_to).format("YYYY-MM-DD") : "Indefinite"}
                      </span>
                    ),
                  },
                  {
                    title: "Priority",
                    dataIndex: "priority",
                    key: "priority",
                    align: "center",
                    width: 80,
                  },
                  {
                    title: "Status",
                    key: "status",
                    align: "center",
                    width: 80,
                    render: (_, record) => (
                      <Tag color={record.is_active ? "success" : "default"}>
                        {record.is_active ? "Active" : "Inactive"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 100,
                    render: (_, record) => (
                      <Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => handleEditRule(record)}
                        />
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          size="small"
                          danger
                          onClick={() => {
                            Modal.confirm({
                              title: "Delete Rule",
                              content: "Are you sure you want to delete this settlement rule?",
                              onOk: () => handleDeleteRule(record.binding_id),
                            });
                          }}
                        />
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          {/* Add/Edit Rule Modal */}
          <Modal
            title={editingRule ? "Edit Settlement Rule" : "Add Settlement Rule"}
            open={ruleModalVisible}
            onCancel={() => setRuleModalVisible(false)}
            onOk={handleSaveRule}
            okText={editingRule ? "Update" : "Add"}
            width={500}
          >
            <Form form={ruleForm} layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item
                name="ruleId"
                label="Settlement Rule"
                rules={[{ required: true, message: "Please select a rule" }]}
              >
                <Select
                  placeholder="Select a settlement rule"
                  disabled={!!editingRule}
                  options={availableRules.map((rule) => ({
                    value: rule.rule_id,
                    label: `${rule.rule_code} - ${rule.name}`,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="paramsOverride"
                label="Parameter Override (JSON)"
                extra="Leave empty to use default parameters"
              >
                <Select
                  mode="tags"
                  placeholder='e.g., {"revenue_share_pct": 15}'
                  style={{ width: "100%" }}
                  tokenSeparators={[]}
                  maxTagCount={1}
                  onChange={(values) => {
                    if (values.length > 0) {
                      try {
                        const parsed = JSON.parse(values[values.length - 1]);
                        ruleForm.setFieldValue("paramsOverride", parsed);
                      } catch (e) {
                        // Invalid JSON, keep as string
                      }
                    }
                  }}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="effectiveFrom"
                    label="Effective From"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="effectiveTo" label="Effective To">
                    <DatePicker style={{ width: "100%" }} placeholder="Indefinite" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    extra="Lower number = higher priority"
                  >
                    <InputNumber min={1} max={999} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="isActive"
                    label="Active"
                    getValueProps={(value) => ({ value: value ? "yes" : "no" })}
                    getValueFromEvent={(v) => v === "yes"}
                  >
                    <Segmented
                      options={[
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        </div>
      ),
    },
  ];

  return (
    <PageLayout active="companySnapshot">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/company/list")}>Companies</a> },
          { title: company.name },
        ]}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/company/list")}>
            Back
          </Button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <Title level={2} style={{ marginBottom: 0 }}>{company.name}</Title>
              <Tag color={company.type === "Aggregator" ? "blue" : "green"}>{company.type}</Tag>
              <Tag color="success">{company.status}</Tag>
            </div>
            <Text type="secondary">
              {company.contactName} | {company.contactEmail} | {company.contactPhone}
            </Text>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </PageLayout>
  );
}
